export type TypeObserveSelectValueConfig = {
    fnChange?: (value: string | string[]) => void;
};

// ----------------------------------------------------------------------------

const modifiedElements = new WeakMap<HTMLSelectElement, PropertyDescriptor>();

// ----------------------------------------------------------------------------

function observeSelectValue(element: HTMLSelectElement, config: TypeObserveSelectValueConfig = {}): () => void {
    if (modifiedElements.has(element)) {
        return (): any => {
            // 'The "value" property has already been observed for this select element'
        };
    }

    const proto = Object.getPrototypeOf(element);
    const original = Object.getOwnPropertyDescriptor(proto, 'value');

    if (!original || typeof original.get !== 'function' || typeof original.set !== 'function') {
        throw new Error('The "value" property does not have valid getter/setter methods');
    }

    const originalGet = original.get.bind(element);
    const originalSet = original.set.bind(element);

    modifiedElements.set(element, original);

    Object.defineProperty(element, 'value', {
        get: function (this: HTMLSelectElement): any {
            return originalGet();
        },
        set: function (this: HTMLSelectElement, newValue: string): any {
            const oldValue = originalGet();

            if (oldValue === newValue) {
                return;
            }

            originalSet(newValue);

            if (typeof config.fnChange === 'function') {
                config.fnChange.call(this, newValue);
            }
        },
        configurable: true,
        enumerable: true,
    });

    return (): any => {
        const originalDescriptor = modifiedElements.get(element);

        if (originalDescriptor) {
            Object.defineProperty(element, 'value', originalDescriptor);

            modifiedElements.delete(element);
        }
    };
}

// ----------------------------------------------------------------------------

export default observeSelectValue;
